// src/components/ChildProfileModal.js
/**
 * Reusable modal for Create/Edit child profiles with tabbed sections.
 * Validates age 2-12 from DOB on the frontend before submitting.
 */
import React, { useState, useEffect } from 'react';

const defaultForm = {
  name: '',
  date_of_birth: '',
  gender: '',
  severity: '',
  primary_language: '',
  secondary_language: '',
  diagnosis: '',
  organisation_id: '',
  guardian_name: '',
  guardian_phone: '',
  referral_source: '',
  medical_history_notes: '',
  intelligibility_level: '',
  stimulability_notes: '',
  phonological_processes: '',
  baseline_score: '',
  therapy_approach: '',
  current_targets: '',
};

function calculateAge(dob) {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

function getAgeGroup(age) {
  if (age === null) return '';
  if (age <= 4) return 'A (2-4 yrs)';
  if (age <= 6) return 'B (5-6 yrs)';
  return 'C (7-12 yrs)';
}

export default function ChildProfileModal({ child, organisations, onSave, onCancel }) {
  const [form, setForm] = useState(defaultForm);
  const [activeTab, setActiveTab] = useState('basic');
  const [ageError, setAgeError] = useState('');
  const [saving, setSaving] = useState(false);

  const isEditing = !!child;

  useEffect(() => {
    if (child) {
      setForm({
        name: child.name || '',
        date_of_birth: child.date_of_birth || '',
        gender: child.gender || '',
        severity: child.severity || '',
        primary_language: child.primary_language || '',
        secondary_language: child.secondary_language || '',
        diagnosis: child.diagnosis || '',
        organisation_id: child.organisation_id || '',
        guardian_name: child.guardian_name || '',
        guardian_phone: child.guardian_phone || '',
        referral_source: child.referral_source || '',
        medical_history_notes: child.medical_history_notes || '',
        intelligibility_level: child.intelligibility_level || '',
        stimulability_notes: child.stimulability_notes || '',
        phonological_processes: child.phonological_processes || '',
        baseline_score: child.baseline_score ?? '',
        therapy_approach: child.therapy_approach || '',
        current_targets: child.current_targets || '',
      });
    } else {
      setForm(defaultForm);
    }
  }, [child]);

  const age = calculateAge(form.date_of_birth);
  const ageGroup = getAgeGroup(age);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'date_of_birth') {
      const newAge = calculateAge(value);
      if (newAge !== null && (newAge < 2 || newAge > 12)) {
        setAgeError(`Age must be between 2-12 years. Calculated: ${newAge} years.`);
      } else {
        setAgeError('');
      }
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      alert('Please enter child name');
      return;
    }
    if (!form.date_of_birth) {
      alert('Date of Birth is required');
      return;
    }
    if (age === null || age < 2 || age > 12) {
      alert(`Child age must be between 2-12 years. Calculated age: ${age} years.`);
      return;
    }
    if (!form.gender) {
      alert('Gender is required');
      return;
    }
    if (!form.primary_language) {
      alert('Primary Language is required');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form };
      // Convert empty strings to null for optional fields
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') payload[key] = null;
      });
      // baseline_score should be a number
      if (payload.baseline_score !== null) {
        payload.baseline_score = parseFloat(payload.baseline_score);
        if (isNaN(payload.baseline_score)) payload.baseline_score = null;
      }
      await onSave(payload);
    } catch (err) {
      // error handled by parent
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'basic', label: ' Basic Info' },
    { id: 'clinical', label: ' Clinical' },
    { id: 'guardian', label: ' Guardian' },
    { id: 'therapy', label: ' Therapy' },
  ];

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content child-profile-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header-bar">
          <h2>{isEditing ? '✏️ Edit' : '➕ Create'} Child Profile</h2>
          <button className="modal-close-btn" onClick={onCancel}>✕</button>
        </div>

        {/* Age Badge */}
        {age !== null && (
          <div className={`age-badge-bar ${ageError ? 'error' : ''}`}>
            <span className="age-display">Age: <strong>{age} years</strong></span>
            <span className="age-group-display">Group: <strong>{ageGroup}</strong></span>
            {ageError && <span className="age-error-msg">⚠️ {ageError}</span>}
          </div>
        )}

        {/* Tabs */}
        <div className="profile-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`profile-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="profile-tab-content">

          {activeTab === 'basic' && (
            <div className="form-grid">
              <div className="form-group required">
                <label>Child Name</label>
                <input type="text" value={form.name} onChange={e => handleChange('name', e.target.value)} placeholder="Enter child name" />
              </div>
              <div className="form-group required">
                <label>Date of Birth</label>
                <input type="date" value={form.date_of_birth} onChange={e => handleChange('date_of_birth', e.target.value)} />
              </div>
              <div className="form-group required">
                <label>Gender</label>
                <select value={form.gender} onChange={e => handleChange('gender', e.target.value)}>
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Severity</label>
                <select value={form.severity} onChange={e => handleChange('severity', e.target.value)}>
                  <option value="">Select</option>
                  <option value="Mild">Mild</option>
                  <option value="Moderate">Moderate</option>
                  <option value="Severe">Severe</option>
                </select>
              </div>
              <div className="form-group">
                <label>Organisation</label>
                <select value={form.organisation_id} onChange={e => handleChange('organisation_id', e.target.value ? parseInt(e.target.value) : '')}>
                  <option value="">Default (Giftabled Foundation)</option>
                  {organisations && organisations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group required">
                <label>Primary Language</label>
                <select value={form.primary_language} onChange={e => handleChange('primary_language', e.target.value)}>
                  <option value="">Select</option>
                  <option value="Hindi">Hindi</option>
                  <option value="English">English</option>
                  <option value="Marathi">Marathi</option>
                  <option value="Gujarati">Gujarati</option>
                  <option value="Tamil">Tamil</option>
                  <option value="Telugu">Telugu</option>
                  <option value="Kannada">Kannada</option>
                  <option value="Bengali">Bengali</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Secondary Language</label>
                <select value={form.secondary_language} onChange={e => handleChange('secondary_language', e.target.value)}>
                  <option value="">None</option>
                  <option value="Hindi">Hindi</option>
                  <option value="English">English</option>
                  <option value="Marathi">Marathi</option>
                  <option value="Gujarati">Gujarati</option>
                  <option value="Tamil">Tamil</option>
                  <option value="Telugu">Telugu</option>
                  <option value="Kannada">Kannada</option>
                  <option value="Bengali">Bengali</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Diagnosis</label>
                <select value={form.diagnosis} onChange={e => handleChange('diagnosis', e.target.value)}>
                  <option value="">Select</option>
                  <option value="Articulation Disorder">Articulation Disorder</option>
                  <option value="Phonological Disorder">Phonological Disorder</option>
                  <option value="Childhood Apraxia of Speech">Childhood Apraxia of Speech</option>
                  <option value="Dysarthria">Dysarthria</option>
                  <option value="Fluency Disorder">Fluency Disorder</option>
                  <option value="Language Delay">Language Delay</option>
                  <option value="Mixed">Mixed</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === 'clinical' && (
            <div className="form-grid">
              <div className="form-group">
                <label>Intelligibility Level</label>
                <select value={form.intelligibility_level} onChange={e => handleChange('intelligibility_level', e.target.value)}>
                  <option value="">Select</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Poor">Poor</option>
                  <option value="Unintelligible">Unintelligible</option>
                </select>
              </div>
              <div className="form-group">
                <label>Baseline Score (0-100)</label>
                <input type="number" min="0" max="100" step="0.1" value={form.baseline_score} onChange={e => handleChange('baseline_score', e.target.value)} placeholder="Initial assessment score" />
              </div>
              <div className="form-group full-width">
                <label>Stimulability Notes</label>
                <textarea rows="3" value={form.stimulability_notes} onChange={e => handleChange('stimulability_notes', e.target.value)} placeholder="Which sounds can the child produce with cueing/modeling?" />
              </div>
              <div className="form-group full-width">
                <label>Phonological Processes</label>
                <textarea rows="3" value={form.phonological_processes} onChange={e => handleChange('phonological_processes', e.target.value)} placeholder="e.g., Fronting, Stopping, Cluster Reduction, Final Consonant Deletion" />
              </div>
            </div>
          )}

          {activeTab === 'guardian' && (
            <div className="form-grid">
              <div className="form-group">
                <label>Guardian Name</label>
                <input type="text" value={form.guardian_name} onChange={e => handleChange('guardian_name', e.target.value)} placeholder="Parent/Guardian name" />
              </div>
              <div className="form-group">
                <label>Guardian Phone</label>
                <input type="tel" value={form.guardian_phone} onChange={e => handleChange('guardian_phone', e.target.value)} placeholder="+91 XXXXX XXXXX" />
              </div>
              <div className="form-group">
                <label>Referral Source</label>
                <select value={form.referral_source} onChange={e => handleChange('referral_source', e.target.value)}>
                  <option value="">Select</option>
                  <option value="School">School</option>
                  <option value="Pediatrician">Pediatrician</option>
                  <option value="ENT Specialist">ENT Specialist</option>
                  <option value="Self-referral">Self-referral</option>
                  <option value="Other Therapist">Other Therapist</option>
                  <option value="NGO">NGO</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group full-width">
                <label>Medical History Notes</label>
                <textarea rows="4" value={form.medical_history_notes} onChange={e => handleChange('medical_history_notes', e.target.value)} placeholder="Hearing loss, cleft palate, developmental delays, etc." />
              </div>
            </div>
          )}

          {activeTab === 'therapy' && (
            <div className="form-grid">
              <div className="form-group">
                <label>Therapy Approach</label>
                <select value={form.therapy_approach} onChange={e => handleChange('therapy_approach', e.target.value)}>
                  <option value="">Select</option>
                  <option value="Articulation Approach">Articulation Approach</option>
                  <option value="Minimal Pairs">Minimal Pairs</option>
                  <option value="Cycles Approach">Cycles Approach</option>
                  <option value="PROMPT">PROMPT</option>
                  <option value="Naturalistic">Naturalistic</option>
                  <option value="Mixed">Mixed</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group full-width">
                <label>Current Targets</label>
                <textarea rows="3" value={form.current_targets} onChange={e => handleChange('current_targets', e.target.value)} placeholder="e.g., /k/ initial, /s/ final, /r/ blends" />
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving || !!ageError}>
            {saving ? '⏳ Saving...' : isEditing ? '💾 Update Child' : '➕ Create Child'}
          </button>
        </div>
      </div>
    </div>
  );
}
